'use client'

import { useReplaceQuery } from '@/util/router'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

export type FilterTabItem = { id: string; text: string }

type Props = {
  id: string
  tabs: FilterTabItem[]
  useSearchParam?: boolean
  value?: string
  onChange?: (value: string) => void
}

export const FilterTab = ({
  id,
  tabs,
  useSearchParam,
  value,
  onChange,
}: Props) => {
  const searchParams = useSearchParams()
  const { replaceQuery } = useReplaceQuery()

  const selected = useMemo(() => {
    if (useSearchParam) {
      return searchParams.get(id) || tabs[0].id
    }

    return value
  }, [useSearchParam, searchParams, value, id, tabs])

  const onChangeTab = (tab: FilterTabItem) => {
    if (useSearchParam) {
      replaceQuery(id, tab.id.toString())
    }

    onChange?.(tab.id)
  }

  return (
    <div className="bg-background flex rounded-lg overflow-hidden shrink-0">
      {tabs.map((tab) => {
        return (
          <li
            key={tab.id}
            className="flex justify-center items-center rounded-lg cursor-pointer gap-0.5 relative py-2 px-2.5"
            onClick={() => onChangeTab(tab)}
          >
            <div
              className={classNames(
                'z-10 flex justify-center items-center duration-500 select-none transition-all',
                {
                  'text-gray-500': selected !== tab.id,
                },
              )}
            >
              {tab.text && (
                <span className="text-body2 font-medium leading-tight">
                  {tab.text}
                </span>
              )}
            </div>
            {selected === tab.id && (
              <motion.div
                className="w-full h-full absolute left-0 pointer-events-none"
                layoutId={`${id}__filter_tab`}
              >
                <div className="bg-gray-200 h-full w-full rounded-lg" />
              </motion.div>
            )}
          </li>
        )
      })}
    </div>
  )
}
